import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {
  Unsubscribe,
  onDisconnect,
  onValue,
  ref,
  remove,
  set,
  update,
} from "firebase/database";

import {useServerTime} from "~/contexts/time/ServerTimeProvider";
import {auth, realtimeDB} from "~/firebase/firebase";

import {useGroup} from "../group/GroupProvider";

const START_TIME_OFFSET = 500;

export interface GroupSessionData {
  id: string;
  hostId: string;
  players: {[uid: string]: true};
  current_round?: {
    roundId: string;
    levelId: string;
    startTime: number;
    participants: {[uid: string]: true};
  };
}

interface GroupSessionContextState {
  status: "LOADING" | "WAITING_FOR_HOST" | "SESSION_ACTIVE" | "ERROR";
  sessionData: GroupSessionData | null;
  error: string | null;
  isHost: boolean;
  startNewGameRound: (levelId: string) => Promise<void>;
}

const GroupSessionContext = createContext<GroupSessionContextState>({
  status: "LOADING",
  sessionData: null,
  error: null,
  isHost: false,
  startNewGameRound: async () => {
    console.error("startNewGameRound called outside of provider");
  },
});

export const GroupSessionProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {groupId} = useGroup();
  const [user] = useAuthState(auth);

  const [sessionData, setSessionData] = useState<GroupSessionData | null>(null);
  const sessionDataRef = useRef(sessionData);
  useEffect(() => {
    sessionDataRef.current = sessionData;
  }, [sessionData]);

  const [status, setStatus] =
    useState<GroupSessionContextState["status"]>("LOADING");
  const [error, setError] = useState<string | null>(null);
  const serverTime = useServerTime();

  const isHost = useMemo(() => {
    if (!user || !groupId) return false;
    return groupId.startsWith(user.uid);
  }, [user, groupId]);

  const startNewGameRound = useCallback(
    async (levelId?: string) => {
      const currentSessionData = sessionDataRef.current;
      if (!isHost || !groupId || !currentSessionData?.players) {
        console.error(
          "Attempted to start a round without being a host or without session data.",
        );
        throw new Error("Cannot start a new round.");
      }

      // Use the current data from the ref, which is always up-to-date.
      if (!currentSessionData?.players) {
        console.error(
          "Attempted to start a round without session player data.",
          sessionDataRef.current,
        );
        throw new Error("Cannot start a new round.");
      }

      console.log(
        `[PROVIDER] Host is starting a new round for level: ${levelId}`,
      );

      const sessionRef = ref(realtimeDB, `game_sessions/${groupId}`);
      const participants = currentSessionData.players; // Use the live data from state

      const newRoundData = {
        roundId: Math.random().toString(36).substring(2, 9),
        levelId,
        startTime: serverTime() + START_TIME_OFFSET,
        participants,
      };

      await update(sessionRef, {current_round: newRoundData});
    },
    [isHost, groupId],
  );

  useEffect(() => {
    if (!user || !groupId) {
      setStatus("LOADING");
      setSessionData(null);
      setError(null);
      return;
    }

    const sessionRef = ref(realtimeDB, `game_sessions/${groupId}`);
    let sessionUnsubscribe: Unsubscribe;

    const setupHost = async () => {
      setStatus("LOADING");
      await onDisconnect(sessionRef).remove();
      const newSession: GroupSessionData = {
        id: groupId,
        hostId: user.uid,
        players: {[user.uid]: true},
      };
      await set(sessionRef, newSession);
      startSubscription();
    };

    const setupClient = () => {
      setStatus("LOADING");
      startSubscription();
    };

    const startSubscription = () => {
      sessionUnsubscribe = onValue(
        sessionRef,
        async (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val() as GroupSessionData;
            setSessionData(data);
            setStatus("SESSION_ACTIVE");

            if (!isHost && user && !data.players[user.uid]) {
              const playerRef = ref(
                realtimeDB,
                `game_sessions/${groupId}/players/${user.uid}`,
              );
              await onDisconnect(playerRef).remove();
              await set(playerRef, true);
            }
          } else {
            setSessionData(null);
            if (isHost) {
              setupHost();
            } else {
              setStatus("WAITING_FOR_HOST");
            }
          }
        },
        (err) => {
          console.error("Firebase subscription error:", err);
          setError("Connection to group session failed.");
          setStatus("ERROR");
        },
      );
    };

    if (isHost) {
      setupHost();
    } else {
      setupClient();
    }

    return () => {
      if (sessionUnsubscribe) {
        sessionUnsubscribe();
      }
      // Graceful exit logic: remove our presence.
      if (user && groupId) {
        if (isHost) {
          // A graceful host exit should remove the entire session.
          remove(sessionRef);
          onDisconnect(sessionRef).cancel(); // Cancel the "will" since we're leaving cleanly.
        } else {
          const playerRef = ref(
            realtimeDB,
            `game_sessions/${groupId}/players/${user.uid}`,
          );
          remove(playerRef);
          onDisconnect(playerRef).cancel();
        }
      }
    };
  }, [groupId, user, isHost]);

  const value = useMemo(
    () => ({
      status,
      sessionData,
      error,
      isHost,
      startNewGameRound,
    }),
    [status, sessionData, error, isHost, startNewGameRound],
  );

  return (
    <GroupSessionContext.Provider value={value}>
      {children}
    </GroupSessionContext.Provider>
  );
};

export const useGroupSession = (): GroupSessionContextState => {
  return useContext(GroupSessionContext);
};
